import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useDialog } from "../../../../components/ui/use-dialog";
import { Button } from "../../../../components/ui/button";
import {
  DialogDescription,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog,
} from "../../../../components/ui/dialog";

import { TIPS_MODES, type TipsMode } from "../../../../constants/tips";
import { navLocale } from "../../../../lib/locale";

export function TipsDialog() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const dialog = useDialog();
  const { t } = useTranslation();

  const mode = React.useMemo(() => {
    return new URLSearchParams(location.search).get("tips") as TipsMode | null;
  }, [location.search]);

  React.useEffect(() => {
    if (mode && TIPS_MODES.includes(mode)) {
      dialog.open();
    }
  }, [location.search, dialog, mode]);

  const onSubmit = () => {
    if (!mode) return;

    const params = new URLSearchParams(location.search);
    params.delete("tips");

    let redirectPath = location.pathname;

    if (mode === "node") {
      redirectPath = navLocale("/data-manager/node");
      queryClient.refetchQueries({ queryKey: ["node-summary-def"] });
      queryClient.refetchQueries({ queryKey: ["node-def"] });
    } else if (mode === "discretize") {
      redirectPath = navLocale("/data-manager/node/discretize");
      queryClient.refetchQueries({ queryKey: ["discretize-def"] });
    } else {
      redirectPath = `${location.pathname}?${params.toString()}`;
    }

    navigate(redirectPath, { replace: true });
    dialog.close();
  };

  if (!mode || !TIPS_MODES.includes(mode)) return null;

  const forceOpen = mode === "node" || mode === "discretize";
  const titleKey = `data-mgmt.dialog.${mode}.title`;
  const descKey = `data-mgmt.dialog.${mode}.desc`;
  const nextKey = `data-mgmt.dialog.${mode}.next`;

  return (
    <Dialog open={dialog.isOpen} onOpenChange={dialog.open}>
      <DialogContent
        className="max-w-[460px] rounded-xl border border-gray-100 dark:border-white/10"
        {...(forceOpen && {
          onInteractOutside: (e: Event) => e.preventDefault(),
          onOpenAutoFocus: (e: Event) => e.preventDefault(),
          hideCloseButton: true,
        })}
      >
        <div className="flex flex-col grow-0 gap-y-5">
          <DialogHeader>
            <DialogTitle>{t(titleKey)}</DialogTitle>
            <DialogDescription>{t(descKey)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="flex flex-row h-8 w-24 px-3 items-center rounded-lg text-xs text-white bg-[#0195c5] hover:bg-[#0195c5]/80"
              onClick={onSubmit}
              variant="headless"
              size="headless"
              type="button"
            >
              {t(nextKey)}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
